import { DataProxy } from 'apollo-cache'
import { dataIdFromObject } from 'graphql/apolloClient'
import {
  ShareListItemFragment,
  ShareListItemFragmentDoc,
  ShareDeleteMembershipMutation,
  GetShareMembershipsDocument,
  GetShareMembershipsQuery,
  ShareMembershipInfoFragment,
} from 'gqlTypes'
import produce from 'immer'
import { ErrorHandler } from 'modules/tracing'

/**
 * TYPES
 */

interface ReadShareListItemFromCache {
  cache: DataProxy
  id: string
}

interface WriteShareListItemToCache {
  cache: DataProxy
  share: ShareListItemFragment
}

interface DeleteShareMembershipWithPreviewProps {
  cache: DataProxy
  data?: ShareDeleteMembershipMutation | null
  share: Partial<ShareListItemFragment>
  member: ShareMembershipInfoFragment
}

/**
 * CACHE HELPERS
 */
export const readShareListItemFromCache = ({
  cache,
  id,
}: ReadShareListItemFromCache) =>
  cache.readFragment<ShareListItemFragment>({
    fragment: ShareListItemFragmentDoc,
    fragmentName: 'ShareListItem',
    id,
  })

export const writeShareListItemToCache = ({
  cache,
  share,
}: WriteShareListItemToCache) =>
  cache.writeFragment<ShareListItemFragment>({
    id: dataIdFromObject(share) as string,
    fragment: ShareListItemFragmentDoc,
    fragmentName: 'ShareListItem',
    data: share,
  })

// Updates cache removing a member for both `GetShareMembershipsQuery` and
// `GetShareMembershipsPreviewQuery`
export const deleteShareMembershipWithPreview = ({
  cache,
  data,
  share,
  member,
}: DeleteShareMembershipWithPreviewProps) => {
  try {
    const query = {
      query: GetShareMembershipsDocument,
      variables: { shareIdentifier: share.identifier },
    }

    const shareMembershipsData = cache.readQuery<GetShareMembershipsQuery>(
      query
    )

    if (!shareMembershipsData) {
      return
    }

    const newData = produce(shareMembershipsData, newData => {
      const memberships = newData.share?.memberships?.entries
      const currentUserMembership = newData.share?.myMembership
      const newMember = data?.shareDeleteMembership?.membership

      if (member.identifier === currentUserMembership?.identifier) {
        newData.share!.myMembership = null
      }

      if (!memberships || !newMember) {
        return
      }

      const newEntries = memberships.filter(
        entry => entry.identifier !== member.identifier
      )
      newData.share!.memberships!.entries = newEntries
      newData.share!.memberships!.meta.totalCount = newEntries.length
    })

    cache.writeQuery<GetShareMembershipsQuery>({
      ...query,
      data: newData,
    })
  } catch (error) {
    // Reading from the cache can throw when the query doesn't exist.
    // We ingore this error because the worst that will happen is that a
    // removed member will not disappear without re-opening the modal.
    ErrorHandler.ignore(error)
  }
}
